import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { Link } from "react-router-dom";
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Breadcrumb from '../common/Breadcrumb';
import { useTasks } from '../../context/TaskContext';
import { useJobs } from '../../context/JobContext';
import { useEstimations } from '../../context/EstimationContext';
import { useTimeOffRequests } from '../../context/TimeOffRequestsContext';
import Api from '../../utils/Api';
import { useAuth } from '../../context/AuthContext';
import { RefreshCcw, UserIcon,HomeIcon,FileTextIcon,CreditCardIcon,ClockIcon,UsersIcon,IdCardIcon,CalendarIcon,Users2Icon,MessageCircleIcon,MapPinIcon } from 'lucide-react';
import GoogleMapComponent from "../common/GoogleMap";
import TaskForm from '../tasks/TaskForm';
import TimeOffRequestsForm from '../time-off-requests/TimeOffRequestsForm';
//import { useGeocode } from "../../hooks/useGeocode";
import { useRoutePath } from '../../utils/getRoutePath';
import { ArrowTopRightOnSquareIcon, PencilIcon,UserGroupIcon,DocumentTextIcon } from '@heroicons/react/24/outline';
  
// Localizer config
const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Format date for display
const formatDate = (dateString, time = false) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day   = String(date.getDate()).padStart(2, '0');
    const year  = date.getFullYear();

    let formatted = `${month}/${day}/${year}`;

    if (time) {
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12; // 12-hour format
      formatted += ` ${hours}:${minutes} ${ampm}`;
    }

    return formatted;
  } catch (error) {
    return 'Invalid Date';
  }
};
  
const formatTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const date = new Date();
  date.setHours(parseInt(hours), parseInt(minutes));
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
 
const handleTouchStart = (e, resource, type, setShowModal) => {
  if (e.cancelable) {
    e.preventDefault(); // only cancel if possible
  }

  const timer = setTimeout(() => {
    setShowModal({ show: true, data: resource, type });
  }, 600);

  const clear = () => clearTimeout(timer);
  e.target.addEventListener("touchend", clear, { once: true });
  e.target.addEventListener("touchmove", clear, { once: true });
};
 
// Event component with enhanced display
const Event = ({ event ,setShowModal}) => (
  <div className="p-1" onTouchStart={(e) => handleTouchStart(e, event?.resource, event.type,setShowModal)} onDoubleClick={() => setShowModal({ show: true, data: event?.resource, type: event.type })}>
    {event.type === 'Task' || event.type === 'Time Off Request' ? (
       <strong className=" text-sm font-semibold text-white">
        {formatDate(event.start, false)} - {formatDate(event.end, false)}
      </strong>
      
    ) : (
      <strong className=" text-sm font-semibold text-white">
        {/* {formatDate(event.start, false)} */}
        {formatTime(event.start_time)} -  {formatTime(event.end_time)}
      </strong>
    )}
    <br></br>
    <strong className="text-sm font-semibold text-white">{event.title}</strong>
    <div className="text-xs text-white opacity-90">Assigned To: {event.assignedTo}</div>
 
    {event.description && (
      <div className="text-xs text-white opacity-90 truncate">
        {event.description}
      </div>
    )}
  </div>
);

const MyCalendar = () => {
  const { tasks, getTasks } = useTasks();
  const { jobs, getJobs } = useJobs();
  const { estimations, getEstimations } = useEstimations();
  const {  getTimeOffRequests } = useTimeOffRequests();
  const [api, setApi] = useState(null);
  const { user, getToken,googleMapApiKey } = useAuth();
  const taskRef = useRef(false);
  const jobRef = useRef(false);
  const estimationRef = useRef(false);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState(null);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [isJobsLoading, setIsJobsLoading] = useState(false);
  const [isEstimationsLoading, setIsEstimationsLoading] = useState(false);
  const [allStatus,setAllStatus] = useState([]);
  const allStatusRef = useRef(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [isTimeOffRequestsLoading, setIsTimeOffRequestsLoading] = useState(false);
  const [showModal, setShowModal] = useState({show: false, data: null, type: null});
  const [animate, setAnimate] = useState(true);
  const getRoutePath = useRoutePath();
  //const { getGeocode } = useGeocode();

  const timeOffRequestRef = useRef(false);
   useEffect(() => {

    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    } 
 

    const fetchStatuses = async () => {
      const response = await apiInstance.call('get-all-status', 'GET');
      
      if(response.status === 200){
        setAllStatus(response?.data || []);
      }
      setStatusLoading(false);
    };

    if(allStatusRef.current === false){
      fetchStatuses();
      allStatusRef.current = true;
     
    }
   
  }, [api,user,allStatus,getToken,setApi,setStatusLoading]);


  const fetchTimeOffRequests = useCallback(async () => {
    setIsTimeOffRequestsLoading(true);
    try {
      const activeTimeOffRequests = await getTimeOffRequests(1, { active: 1, completed: '0', all: true });
  
      setEvents(prevEvents => {
        // Keep non-time-off-request events
        const nonTimeOffRequestEvents = prevEvents.filter(event => !event.id.startsWith('time-off-request-'));
        let timeOffRequestEvents = [];
        
        if (Array.isArray(activeTimeOffRequests?.data) && activeTimeOffRequests.data.length) {
          timeOffRequestEvents = activeTimeOffRequests.data.reduce((acc, request) => {
            if (!request.start_date || !request.end_date) return acc;
          
            try {
              const startDate = new Date(request.start_date);
              const endDate = new Date(request.end_date);
  
              // Apply end time if available
              if (request.end_time) {
                const [hours, minutes] = request.end_time.split(':').map(Number);
                if (!isNaN(hours) && !isNaN(minutes)) {
                  endDate.setHours(hours, minutes);
                }
              }
  
              // Handle assigned users safely
              let assignedTo = 'Unassigned';
              if (Array.isArray(request.assign_to) && request.assign_to.length) {
                assignedTo = request.assign_to
                  .map(user => user?.name || user?.first_name || 'Unknown')
                  .join(', ');
              }
  
              acc.push({
                id: `time-off-request-${request.id}`,
                title: `Time Off Request`,
                start: startDate,
                end: endDate,
                assignedTo,
                type: 'Time Off Request',
                description: request?.description,
                color: '#707777',
                resource: request,
                
              });

            
            } catch (err) {
              console.error('Error processing time off request:', request, err);
            }
  
            return acc;
          }, []);
        }
  
        return timeOffRequestEvents.length ? [...nonTimeOffRequestEvents, ...timeOffRequestEvents] : nonTimeOffRequestEvents;
      });
  
    } catch (error) {
      console.error('Error fetching time off requests:', error);
    } finally {
      setIsTimeOffRequestsLoading(false);
    }
  }, [getTimeOffRequests]);
  

   const fetchTasks = useCallback(async () => {
   
    setIsTasksLoading(true);
    try {
    
        const activeTasks = await getTasks(1, { active: 1,completed: '0', all: true });
          // Update events with tasks
      
          setEvents(prevEvents => {
            // Keep non-task events
            const nonTaskEvents = prevEvents.filter(event => !event.id.startsWith('task-'));
            let taskEvents = [];
           
            if (Array.isArray(activeTasks?.data) && activeTasks.data.length) {
             
              taskEvents = activeTasks.data.reduce((acc, task) => {
                if (!task.start_date || !task.end_date) return acc;
          
                try {
                  const startDate = new Date(task.start_date);
                  const endDate = new Date(task.end_date);
          
                  // Validate dates
                  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return acc;
          
                  // Apply end time if available
                  if (task.end_time) {
                    const [hours, minutes] = task.end_time.split(':').map(Number);
                    if (!isNaN(hours) && !isNaN(minutes)) {
                      endDate.setHours(hours, minutes);
                    }
                  }
          
                  // Handle assigned users safely
                  let assignedTo = 'Unassigned';
                  if (Array.isArray(task.assign_to) && task.assign_to.length) {
                    assignedTo = task.assign_to
                      .map(user => user?.name || user?.first_name || 'Unknown')
                      .join(', ');
                  }
          
                  acc.push({
                    id: `task-${task.id}`,
                    title: `Task`,
                    start: startDate,
                    end: endDate,
                    assignedTo,
                    type: 'Task',
                    description:  task?.description,
                    color: '#707070',
                    resource: task
                  });
                } catch (err) {
                  console.error('Error processing task:', task, err);
                }
          
                return acc;
              }, []);
            }
          
            return taskEvents.length ? [...nonTaskEvents, ...taskEvents] : nonTaskEvents;
          });
          
      
         } catch (error) {
       // Error fetching tasks
     } finally {
      setIsTasksLoading(false);
    }
  }, [getTasks]);

  const fetchJobs = useCallback(async () => {
    setIsJobsLoading(true);
    try {
        const activeJobs = await getJobs(1, { active: 1, all: true});
      
       // Update events with jobs
       setEvents(prevEvents => {
        const nonJobEvents = prevEvents.filter(event => !event.id.startsWith('job-'));
        const jobEvents = [];
        
          if (activeJobs && Array.isArray(activeJobs?.data)) {
          
           activeJobs?.data.forEach(job => {
             // console.log('Processing job:', job); // REMOVED to reduce noise
            if (job.onsite_visit_date) {
              try {
                const visitStartDate = new Date(job.start_date);
                
                                 // Validate date
                 if (isNaN(visitStartDate.getTime())) {
                   return;
                 }
                let visitEndDate = ''
                if(!job.end_date){
                  visitEndDate = new Date(job.start_date);
                }else{
                  visitEndDate = new Date(job.end_date);
                }
                 
                                 // Validate date
                 if (isNaN(visitEndDate.getTime())) {
                   return;
                 }
                
                // Set start time to 9 AM and end time to 5 PM for the visit date
                const startDate = new Date(visitStartDate);
                if (job?.arrival_start) {
                  const [h, m] = job.arrival_start.split(':').map(Number);
                  startDate.setHours(h, m);
                }
                
                const endDate = new Date(visitEndDate);
                if (job?.arrival_end) {
                  const [h, m] = job.arrival_end.split(':').map(Number);
                  endDate.setHours(h, m);
                }  
                
                const jobEvent = {
                  id: `job-${job.id}`,
                  title: `Job: ${job.job_number || 'No Job Number'}`,
                  start: startDate,
                  end: endDate,
                  start_time:job?.arrival_start,
                  end_time:job?.arrival_end,
                  assignedTo: job.assign_techs ? 
                    (Array.isArray(job.assign_techs) ? 
                      job.assign_techs.map(tech => tech?.first_name + ' ' + tech?.last_name || 'Unknown').join(', ') : 
                      'Assigned Techs') : 'Unassigned',
                  type: 'Job',
                  description: job?.description,
                  color: job?.job_status?.color,
                  resource: job
                };
                               // console.log('Created job event:', jobEvent); // REMOVED to reduce noise
                 jobEvents.push(jobEvent);
              } catch (jobError) {
                console.error('Error processing job:', job, jobError);
              }
                         } else {
               // Job missing visit date
             }
          });
                 } else {
           // No active jobs found or invalid data structure
         }
        
        const newEvents = [...nonJobEvents, ...jobEvents];
         return newEvents;
      });
    } catch (error) {
       // Error fetching jobs
     } finally {
      setIsJobsLoading(false);
    }
  }, [getJobs]);

  const fetchEstimations = useCallback(async () => {
    setIsEstimationsLoading(true);
    try {
        const activeEstimations = await getEstimations(1, { active: 1, all: true });
        // Update events with estimations
       setEvents(prevEvents => {
        const nonEstimationEvents = prevEvents.filter(event => !event.id.startsWith('estimation-'));
        const estimationEvents = [];
        
          if (activeEstimations && Array.isArray(activeEstimations?.data)) {
             
           activeEstimations?.data.forEach(estimation => {
                
            if (estimation.onsite_visit_date) {
              try {
                const visitDate = new Date(estimation.onsite_visit_date);
                
                // Validate date
                if (isNaN(visitDate.getTime())) {
                  return;
                }
                
                // Set start time to 10 AM and end time to 4 PM for the visit date
               
                 const startDate = new Date(visitDate);
                 if (estimation?.arrival_start) {
                   const [h, m] = estimation.arrival_start.split(':').map(Number);
                   startDate.setHours(h, m);
                 }
                 
                 const endDate = new Date(visitDate);
                 if (estimation?.arrival_end) {
                   const [h, m] = estimation.arrival_end.split(':').map(Number);
                   endDate.setHours(h, m);
                 }  
                
                const estimationEvent = {
                  id: `estimation-${estimation.id}`,
                  title: `Estimation: ${estimation.estimation_number || 'No Estimation Number'}`,
                  start: startDate,
                  end: endDate,
                  start_time:estimation?.arrival_start,
                  end_time:estimation?.arrival_end,
                  assignedTo: estimation.assign_techs ? 
                    (Array.isArray(estimation.assign_techs) ? 
                      estimation.assign_techs.map(tech => tech?.first_name + ' ' + tech?.last_name || 'Unknown').join(', ') : 
                      'Assigned Techs') : 'Unassigned',
                  type: 'Estimation',
                  description: estimation?.description,
                  color: estimation?.estimation_status?.color,
                  resource: estimation
                };
                
                                 // console.log('Created estimation event:', estimationEvent); // REMOVED to reduce noise
                 estimationEvents.push(estimationEvent);
              } catch (estimationError) {
                console.error('Error processing estimation:', estimation, estimationError);
              }
                         } else {
               // Estimation missing visit date
             }
          });
        } else {
           // No active estimations found or invalid data structure
         }
        
          const newEvents = [...nonEstimationEvents, ...estimationEvents];
         return newEvents;
      });
         } catch (error) {
       // Error fetching estimations
     } finally {
      setIsEstimationsLoading(false);
    }
  }, [getEstimations]);

     // Separate useEffect for Tasks
   useEffect(() => {
     if(taskRef.current === false){
       fetchTasks();
       taskRef.current = true;
     }
   }, [fetchTasks]); // Include fetchTasks dependency - refs prevent infinite loops

     // Separate useEffect for Jobs
   useEffect(() => {
     if(jobRef.current === false){
       fetchJobs();
       jobRef.current = true;
     }
   }, [fetchJobs]); // Include fetchJobs dependency - refs prevent infinite loops

     // Separate useEffect for Estimations
   useEffect(() => {
    if(estimationRef.current === false){
       fetchEstimations();
       estimationRef.current = true;
     }
   }, [fetchEstimations]); // Include fetchEstimations dependency - refs prevent infinite loops

   useEffect(() => {
    if(timeOffRequestRef.current === false){
      fetchTimeOffRequests();
      timeOffRequestRef.current = true;
    }
   }, [fetchTimeOffRequests]); // Include fetchTimeOffRequests dependency - refs prevent infinite loops

     // Combined loading state
   useEffect(() => {
     const allLoading = isTasksLoading || isJobsLoading || isEstimationsLoading || isTimeOffRequestsLoading;
     setIsLoading(allLoading);
     
     if (!allLoading) {
       // Use setTimeout to avoid dependency issues while still logging events
       setTimeout(() => {
        
       }, 100);
     }
   }, [isTasksLoading, isJobsLoading, isEstimationsLoading, isTimeOffRequestsLoading, events.length]); // Include events.length dependency

  // Debug events state changes - REMOVED to prevent loops
  // const geoCodeRef = useRef(false);

  // useEffect(() => {
  //   if (showModal?.show) {
  //     const fetchCoords = async () => {
  //       try {
  //         const locations = showModal?.data?.customer?.service_locations || [];
           
  //         const results = await Promise.all(
  //           locations.map(loc => getGeocode(loc?.street_address))
  //         );
  //         console.log(results)
         
  //       } catch (err) {
  //         console.error("Error getting geocode:", err);
  //       }
  //     };
  
  //     if (geoCodeRef.current === false) {
  //       fetchCoords();   
  //       geoCodeRef.current = true;
  //     }
  //   }
  // }, [showModal, getGeocode]);
   

  useEffect(() => {
    let delay = 300;
  
    // Use AND instead of OR
    if (showModal?.type !== 'Job' && showModal?.type !== 'Estimation') {
      delay = 3000;
    }
  
    if (showModal?.show) {
      setAnimate(true); // start animation
      const timeout = setTimeout(() => {
        setAnimate(false); // remove animation after delay
      }, delay);
  
      return () => clearTimeout(timeout); // cleanup if modal closes early
    }
  }, [showModal?.show, showModal]);
  
      
   return (
    <>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar (col-md-2 equivalent) */}
          <div className="col-span-12 md:col-span-2 bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-semibold mb-2">To Schedule</h2>
              
            <ul className="divide-y divide-gray-200 text-gray-700 w-full">
              
              <li className="py-2">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer hover:text-blue-600">
                    <span>Jobs</span>
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {jobs?.data?.length || 0}
                    </span>
                  </summary>
                  <ul className="ml-4 mt-2 space-y-1 text-sm text-gray-600 max-h-60 overflow-y-auto scrollbar-thin">
                    {jobs?.data?.map(job => (
                      <li onTouchStart={(e) => handleTouchStart(e, job, 'Job',setShowModal)} onDoubleClick={() => setShowModal({show: true, data:job,'type':'Job' })} key={job.id} className="hover:text-blue-500 cursor-pointer bg-red-50 p-2 rounded">
                        <p>Job #: {job.job_number || 'N/A'}</p>
                        <p>Assigned To: {job.assign_techs ? 
                          (Array.isArray(job.assign_techs) ? 
                          job.assign_techs.map(tech =>  tech?.first_name+' '+tech?.last_name || 'Unknown').join(', ') : 
                            'Assigned Techs') : 'Unassigned'}</p>
                        <p>Start & End Dates: {formatDate(job.start_date,false)} {job.end_date ? `- ${formatDate(job.end_date,false)}` : ''}</p>
                        <p>Arrival Start: {formatTime(job.arrival_start)}</p>
                        <p>Arrival End: {formatTime(job.arrival_end)}</p>
                        <p>Description: {job.description || 'No Description'}</p>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>

              <li className="py-2">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer hover:text-blue-600">
                    <span>Tasks</span>
                    <span className="bg-[#3174ad] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {tasks?.data?.length || 0}
                    </span>
                  </summary>
                  <ul className="ml-4 mt-2 space-y-1 text-sm text-gray-600 max-h-60 overflow-y-auto scrollbar-thin">
                    {tasks?.data?.map(task => (
                      <li onTouchStart={(e) => handleTouchStart(e, task, 'Task',setShowModal)} onDoubleClick={() => setShowModal({show: true, data:task,'type':'Task' })} key={task.id} className="hover:text-blue-500 cursor-pointer bg-blue-50 p-2 rounded">
                        <p>Assigned To: {task.assign_to?.map(user => user.name || user.first_name || 'Unknown').join(', ') || 'Unassigned'}</p>
                        <p>Start: {formatDate(task.start_date,false)}</p>
                        <p>End: {formatDate(task.end_date,false)}</p>
                        <p>End Time: {formatTime(task?.end_time)}</p>
                        <p>Description: {task.description || 'No Description'}</p>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>

              <li className="py-2">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer hover:text-blue-600">
                    <span>Estimations</span>
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {estimations?.data?.length || 0}
                    </span>
                  </summary>
                  <ul className="ml-4 mt-2 space-y-1 text-sm text-gray-600 max-h-60 overflow-y-auto scrollbar-thin">
                    {estimations?.data?.map(estimation => (
                      <li onTouchStart={(e) => handleTouchStart(e, estimation, 'Estimation',setShowModal)} onDoubleClick={() => setShowModal({show: true, data:estimation,'type':'Estimation' })} key={estimation.id} className="hover:text-blue-500 cursor-pointer bg-green-50 p-2 rounded">
                        <p>Estimation #: {estimation.estimation_number || 'N/A'}</p>
                        <p>Assigned To: {estimation.assign_techs ? 
                          (Array.isArray(estimation.assign_techs) ? 
                          estimation.assign_techs.map(tech =>  tech?.first_name+' '+tech?.last_name || 'Unknown').join(', ') : 
                            'Assigned Techs') : 'Unassigned'}</p>
                        <p>Visit Date: {formatDate(estimation.onsite_visit_date,false)} {formatTime(estimation.arrival_start)} - {formatTime(estimation.arrival_end)}</p>
                        <p>Description: {estimation.description || 'No Description'}</p>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            </ul>
  
            <h2 className="text-lg font-semibold mb-2 mt-3">Statuses</h2>

            <ul className="ml-4 mt-2 text-sm text-gray-600 max-h-60 overflow-y-auto scrollbar-thin">
              {statusLoading ? (
              <>
              {[...Array(4)].map((_, i) => (
                <li key={i} className="py-1 rounded animate-pulse">
                  <div className="flex items-center space-x-2">
                    {/* Color box placeholder */}
                    <div className="w-3 h-3 bg-gray-300 rounded-sm"></div>
          
                    {/* Text placeholder */}
                    <div className="h-3 w-24 bg-gray-300 rounded"></div>
                  </div>
                </li>
              ))}
            </>
              
              ) : (
                <>
                  {/* Estimation Status */}
                  {allStatus?.estimationStatus?.data?.map((status, index) => (
                    <li
                      key={`est-${index}`}
                      className="hover:text-blue-500 cursor-pointer py-1 rounded"
                    >
                      <div className="flex items-center space-x-2">
                        {/* Color box */}
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: status.color }}
                        ></div>

                        {/* Status name */}
                        <span className="calendar-font-one calendar-line-height-p">
                          {status.name}
                        </span>
                      </div>
                    </li>
                  ))}

                  {/* Job Status */}
                  {allStatus?.jobStatus?.data?.map((jobStatus, i) =>
                    jobStatus.statuses.map((status, j) => (
                      <li
                        key={`job-${i}-${j}`}
                        className="hover:text-blue-500 cursor-pointer py-1 rounded"
                      >
                        <div className="flex items-center space-x-2">
                          {/* Color box */}
                          <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: status.color }}
                          ></div>

                          {/* Status name */}
                          <span className="calendar-font-one calendar-line-height-p">
                            {status.name}
                          </span>
                        </div>
                      </li>
                    ))
                  )}
                </>
              )}
            </ul>

          </div>

          {/* Main Calendar (col-md-10 equivalent) */}
          <div className="col-span-12 md:col-span-10 bg-white rounded-lg shadow-md p-4">
            {/* Status and Refresh Section */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Status: </span> 
                 
                  {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </div>
                    ) : error ? (
                      <span className='text-red-500'>Error</span>
                    ) : (
                      <span className='text-green-500'>Ready</span>
                    )}

                </div>
                  <button
                   onClick={() => { 
                    fetchTasks();
                    fetchJobs();
                    fetchEstimations();
                    fetchTimeOffRequests();
                  }}
                   disabled={isLoading}
                   className="px-4 py-2 bg-[#009efb] text-white rounded-md hover:bg-[#009efb] disabled:bg-gray-400 disabled:cursor-not-allowed"
                 >
                  {isLoading ? (
                    <>
                     <div className='flex items-center'>
                       <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                       Loading...
                    </div>
  
                    </>
                  ) : (
                    <>
                      <div className='flex items-center'>
                         <RefreshCcw className="w-4 h-4 mr-2" />
                         Refresh
                      </div>
                    </>
                  )}
                 </button>
                                    
              </div>
              {error && (
                <div className="mt-2 text-sm text-red-600">
                  <span className="font-semibold">Error:</span> {error}
                </div>
              )}
            </div>

            {isLoading ? (
              <>
               {/* Top Skeleton Bar */}
                <div className="flex justify-between p-2 mb-3 animate-pulse">
                  {/* Left 3 Buttons */}
                  <div className="flex space-x-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={`left-${i}`}
                        className="h-8 w-20 bg-gray-100 rounded-md"
                      ></div>
                    ))}
                  </div>

                  {/* Right 3 Buttons */}
                  <div className="flex space-x-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={`right-${i}`}
                        className="h-8 w-20 bg-gray-100 rounded-md"
                      ></div>
                    ))}
                  </div>
                </div>
                
                {/* Calendar Grid Skeleton */}
                <div className="grid grid-cols-6 gap-2 h-64 p-2 animate-pulse">
                  {[...Array(18)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-gray-100 rounded-md h-20"
                    ></div>
                  ))}
                </div>
            </>
           
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-64 text-red-600">
                <p>{error}</p>
                <button
                  onClick={() => {
                    fetchTasks();
                    fetchJobs();
                    fetchEstimations();
                    fetchTimeOffRequests();
                  }}
                  className="px-4 py-2 bg-[#009efb] text-white rounded-md hover:bg-[#009efb] disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Refresh Data
                </button>
              </div>
            ) : (
              <>
                {events.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <p className="text-lg mb-2">No events found</p>
                    <p className="text-sm mb-4">Try refreshing the data or check if there are active tasks, jobs, or estimations</p>
                    <button
                     onClick={() => {
                      fetchTasks();
                      fetchJobs();
                      fetchEstimations();
                      fetchTimeOffRequests();
                    }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      Refresh Data
                    </button>
                  </div>
                ) : (
                 
                  <BigCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 1000 }}
                    components={{
                      event: (props) => <Event {...props} setShowModal={setShowModal} />,
                    }}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: event.color,
                        border: 'none',
                        borderRadius: '4px',
                        color: 'white',
                        padding: '2px',
                      },
                    })}
                    onSelectEvent={(event) => {
                      //console.log(event)
                       // You can add modal or navigation logic here
                     }}
                    tooltipAccessor={(event) => `${event.title}\nAssigned To: ${event.assignedTo}\nType: ${event.type}`}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {showModal.show && ( 
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity">
          {/* Modal content */}
          <div className="bg-white rounded-2xl shadow-2xl w-full h-full sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-4xl lg:max-w-7xl p-6 relative transform transition-all scale-95 animate-fadeIn flex flex-col">

            
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {showModal?.type?.charAt(0).toUpperCase() + showModal?.type?.slice(1)}
                {showModal?.type === 'Job' && ` #${showModal?.data?.job_number}`}
                {showModal?.type === 'Estimation' && ` #${showModal?.data?.estimation_number}`}
                 
              </h2>
              <button
                onClick={() => setShowModal({ show: false, data: [] })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
      
            {/* Body */}
            <div className={`${animate ? "animate-pulse blur-sm" : ""} text-gray-600 w-full max-w-7xl max-h-[90vh] overflow-y-auto scrollbar-thin pr-1`}>
             
              {showModal?.data ? (
                <div className="space-y-2 text-sm">
                  
                  {(showModal?.type === 'Job' || showModal?.type === 'Estimation') && (
                    <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
                       
                       <div className="w-full sm:w-1/5 ">

                        <div className='border px-3 py-2 rounded-lg'>
                          <p className="text-sm font-bold">Current Status</p>

                          {showModal?.type === 'Job' && (
                            <p style={showModal?.data?.job_status?.style}
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">{showModal?.data?.job_status?.name}</p>
                          )}
                          {showModal?.type === 'Estimation' && (
                            <p style={showModal?.data?.estimation_status?.style}
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">{showModal?.data?.estimation_status?.name}</p>
                          )}
                        </div>

                        <div className='border px-3 py-2 rounded-lg self-end mt-3'>
                          <Link target='_blank' className='w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200 ' to={getRoutePath(`/${showModal?.type.toLowerCase()}s/view/${showModal?.data?.id}`)}>
                          <ArrowTopRightOnSquareIcon className='w-4 h-4 mr-2' /> View Details
                          </Link>
                        </div>

                        <div className='border px-3 py-2 rounded-lg self-end mt-3'>
                          <Link target='_blank' className='w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200 ' to={getRoutePath(`/${showModal?.type.toLowerCase()}s/edit/${showModal?.data?.id}`)}>
                          <PencilIcon className='w-4 h-4 mr-2' /> Edit Details
                          </Link>
                        </div>

                      </div>

                      <div className="w-full sm:w-1/2 ">
                          <div className='border px-3 py-2 rounded-lg'>
                             
                            <div className="flex flex-row justify-between items-start gap-4">
                              <div className="flex items-center font-bold">
                                <UserIcon className="w-4 h-4 mr-2" />
                                Customer
                              </div>
                              <span>{showModal?.data?.customer?.name}</span>
                            </div>
                            
                            <hr className='my-4'/>
    
                            <div className="flex flex-row justify-between items-start gap-4">
                              <div className="flex items-center font-bold">
                                <UserIcon className="w-4 h-4 mr-2" />
                                Primary Contact
                              </div>
                              <span className='flex flex-col justify-end items-end'>
                                <div className="flex flex-col items-end">
                                <span>{showModal?.data?.primary_contact?.first_name} {showModal?.data?.primary_contact?.last_name}</span>  
                                  <span>{showModal?.data?.primary_contact?.phone_numbers[0]?.number}</span>
                                  <span>{showModal?.data?.primary_contact?.emails[0].address}</span>
                                </div>
                              </span>
                              
                            </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4">
                              <div className="flex items-center font-bold">
                                <HomeIcon className="w-4 h-4 mr-2" />
                                Service Location
                              </div>

                              <span className='flex flex-col justify-end items-end'>
                              <div className="flex flex-col items-end">
                              {showModal?.data?.service_locations?.map((loc) => (
                                <div key={loc.id} className="flex justify-between items-end gap-2 mb-2">
                                  <span className='flex items-center  gap-1'>
                                    <MapPinIcon className="w-4 h-4" />
                                    <span>{loc.street_address}, {loc.city}, {loc.state}</span>
                                  </span>
                                </div>
                              ))}
                              </div> 
                            </span>

                            </div>
  
                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4">
                              <div className="flex items-center font-bold">
                                <FileTextIcon className="w-4 h-4 mr-2" />
                                Description
                              </div>
                              <span className='flex flex-col justify-end items-end'>
                                <div className="flex flex-col items-end">
                                  <span>{showModal?.data?.description || '-'}</span>
                                    </div>
                                  </span>
                            </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4">
                              <div className="flex items-center font-bold">
                                <FileTextIcon className="w-4 h-4 mr-2" />
                                PO #
                              </div>
                                <span className='flex flex-col justify-end items-end'>
                                <div className="flex flex-col items-end">
                                  <span>{showModal?.data?.po_number || '-'}</span>
                                </div>
                                </span>
                            </div>
      
                        </div>
                        {showModal?.type === 'Job' && (
                           <div className='border px-3 py-2 rounded-lg mt-3'>

                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <CreditCardIcon className="w-4 h-4 mr-2" />
                                  Paid By
                                </div>
                                <span>
                                  Direct Bill
                                </span>
                              </div>

                              <hr className='my-4'/>

                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <IdCardIcon className="w-4 h-4 mr-2" />
                                  Cheque/Ref#
                                </div>
                                <span className='text-gray-500'>
                                  Not Set
                                </span>
                              </div>

                              <hr className='my-4'/>

                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <UsersIcon className="w-4 h-4 mr-2" />
                                  Teams
                                </div>
                                <span>
                                  Dur
                                </span>
                              </div>
                              
                              <hr className='my-4'/>

                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <ClockIcon className="w-4 h-4 mr-2" />
                                  Status
                                </div>
                                <span>
                                  Paid
                                </span>
                              </div>
 
                          </div>  
                        )}   

                      </div>         
                

                      <div className="w-full sm:w-1/4 max-w-xs h-24 mt-4 sm:mt-0 sm:ml-0">

                        <GoogleMapComponent
                          googleMapApiKey={googleMapApiKey}
                          containerStyle={{
                            width: "100%",
                            height: "200px",
                          }}
                          // addresses={showModal?.data?.service_locations?.map(
                          //   (loc) => `${loc.lat || ""}, ${loc.lng || ""}`
                          // )}
                        addresses={showModal?.data?.service_locations?.map(loc =>
                          `${loc.street_address || ""}, ${loc.city || ""}, ${loc.state || ""}, ${loc.country || ""}`
                        )}
                        />

                        <div className='border px-3 py-2 rounded-lg mt-3'>
                              
                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {showModal?.type === 'Job' ? 'Current Status' : showModal?.type === 'Estimation' ? 'Current Status' : ''}
                                </div>
                                {showModal?.type === 'Job' ? (
                                  <span className={`text-${showModal?.data?.job_status?.color}`}>
                                    {showModal?.data?.job_status?.name}
                                  </span>
                                ) : showModal?.type === 'Estimation' ? (
                                  <span className={`text-${showModal?.data?.estimation_status?.color}`}>
                                    {showModal?.data?.estimation_status?.name}
                                  </span>
                                ) : null}
  
                              </div>

                              <hr className='my-4'/>

                              <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                <div className="flex items-center font-bold">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  Start & End Dates
                                </div>
                                <span className='text-right'>
                                  {showModal?.type === 'Job' ? (
                                    <>
                                      {formatDate(showModal?.data?.start_date,false)} {showModal?.data?.end_date ? `- ${formatDate(showModal?.data?.end_date,false)}` : ''} 
                                    </>
                                  ) : showModal?.type === 'Estimation' ? (
                                    <>
                                      {formatDate(showModal?.data?.onsite_visit_date)}
                                    </>
                                  ) : null}
                                </span>
                              </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4 text-sm">
                              <div className="flex items-center font-bold">
                                <ClockIcon className="w-4 h-4 mr-2" />
                                Arrival Time Window
                              </div>
                              <span>
                              {showModal?.data?.arrival_start} - {showModal?.data?.arrival_end }
                              </span>
                            </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4 text-sm">
                              <div className="flex items-center font-bold">
                                <ClockIcon className="w-4 h-4 mr-2" />
                                Duration
                              </div>
                              <span>
                              {showModal?.data?.duration_h}h  {showModal?.data?.duration_m}m
                               </span>
                            </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4 text-sm">
                              <div className="flex items-center font-bold">
                                <Users2Icon className="w-4 h-4 mr-2" />
                                Assigned Techs
                              </div>
                                
                              
                              <div className="flex flex-col items-end">
                                {showModal?.data?.assign_techs?.map(tech => (
                                  <div key={tech.id} className="flex items-center space-x-1">
                                    <UserIcon className="w-4 h-4" />
                                    <span>{tech.first_name} {tech.last_name}</span>
                                  </div>
                                ))}
                              </div>
 
                            </div>

                            <hr className='my-4'/>

                            <div className="flex flex-row justify-between items-start gap-4 text-sm">
                              <div className="flex items-center font-bold">
                                <MessageCircleIcon className="w-4 h-4 mr-2" />
                                Notes for Techs
                              </div>
                                 
                              <div className="flex flex-col items-end">
                               {showModal?.data?.note_for_tech || '-'}
                              </div>
                           </div>

                            {(showModal?.type === 'Job' || showModal?.type === 'Estimation') && (
                              <>
                                <hr className="my-4" />
                                <div className="text-sm">
                                  <div className="flex items-center font-bold mb-3">
                                    <ClockIcon className="w-4 h-4 mr-2" />
                                    Additional Site Visits ({showModal?.data?.site_visits?.length || 0})
                                  </div>

                                  <div className="space-y-4">
                                    {showModal?.data?.site_visits?.map((siteVisit, index) => (
                                      <div
                                        key={siteVisit.id}
                                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                      >
                                        <div className="flex-1 space-y-2">
                                          {/* Date */}
                                          <div className="flex items-center gap-2">
                                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                                            <span className="text-sm font-medium text-gray-900">
                                              {siteVisit.onsite_visit_date || siteVisit.start_date}
                                            </span>
                                          </div>

                                          {/* Time */}
                                          <div className="flex items-center gap-2">
                                            <ClockIcon className="h-4 w-4 text-gray-500" />
                                            <span className="text-sm text-gray-600">
                                              {siteVisit.arrival_start} - {siteVisit.arrival_end}
                                            </span>
                                          </div>

                                          {/* Duration */}
                                          {(siteVisit.duration_h || siteVisit.duration_m) && (
                                            <div className="flex items-center gap-2">
                                              <ClockIcon className="h-4 w-4 text-gray-500" />
                                              <span className="text-sm text-gray-600">
                                                Duration: {siteVisit.duration_h || 0}h {siteVisit.duration_m || 0}m
                                              </span>
                                            </div>
                                          )}

                                          {/* Techs */}
                                          {siteVisit.assigned_tech_ids?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                              <UserGroupIcon className="h-4 w-4 text-gray-500" />
                                              <span className="text-sm text-gray-600">
                                                Techs:{" "}
                                                {siteVisit.assigned_techs
                                                  .map((tech) => `${tech.first_name} ${tech.last_name}`)
                                                  .join(", ")}
                                              </span>
                                            </div>
                                          )}

                                          {/* Notes */}
                                          {siteVisit.note_for_tech && (
                                            <div className="flex items-start gap-2">
                                              <DocumentTextIcon className="h-4 w-4 text-gray-500 mt-0.5" />
                                              <span className="text-sm text-gray-600">
                                                {siteVisit.note_for_tech}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </>
                            )}
 
                            {showModal?.type === 'Job' && (
                              <>
                                <hr className="my-4" />

                                <div className="flex flex-row justify-between items-start gap-4 text-sm">
                                  <div className="flex items-center font-bold">
                                    <ClockIcon className="w-4 h-4 mr-2" />
                                    Completion Notes
                                  </div>
                                  <span>
                                    {showModal?.data?.completion_notes || '-'}
                                  </span>
                                </div>
                              </>
                            )}
                         </div>

                      </div>
                    </div>
                   
                  )}
 
                  {showModal?.type === 'Task' && (
                    <div>
                      <TaskForm cData={showModal?.data} />
                    </div>
                  )}
                  
                  {showModal?.type === 'Time Off Request' && (
                    <div>
                      <TimeOffRequestsForm cData={showModal?.data} />
                    </div>
                  )}
 
                
                </div>
              ) : (
                <p className="text-gray-500 italic">No data found.</p>
              )}
            </div>
      
            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3 border-t pt-3">
              
            </div>
          </div>
        </div>
        )}

      </div>
    </>
  );
};

export default MyCalendar;
